import { faBell as farBell, faStar as farStar } from '@fortawesome/pro-regular-svg-icons';
import {
    faBell as fasBell,
    faInfoCircle,
    faLock,
    faSearch,
    faStar as fasStar,
    faUnlockAlt,
    faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { Location } from 'history';
import {
    num_POSTS_AND_ANSWERS_IN_LAST_WEEK,
    GROUP_ASK_TO_JOIN,
    GROUP_ASKED_TO_JOIN,
    GROUP_YOU_ARE_BLOCKED,
    JOIN_GROUP,
    LEAVE_GROUP,
} from 'mk/autogenerated/translations/GroupHeader.03c26c004c5503fb8f151213454c063d'
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import { groupsGroupInfoUrl } from 'mk2/apps/groups/urls';
import { loginUrl, signupUrl } from 'mk2/apps/users/urls';
import { getLastKnownUsernameCookie } from 'mk2/apps/users/utils';
import { Btn,  BtnCountType, BtnType } from 'mk2/components/Btn';
import { Cover } from 'mk2/components/Cover';
import { Interpolate } from 'mk2/components/Interpolate';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { BlogPostEntity, GroupEntity, GroupMemberEntity, UserEntity } from 'mk2/schemas';
import { parse } from 'query-string';
import React from 'react';
import { SAME_DATE_GROUP_CATEGORY_ID } from '../containers/GroupFeed/GroupFeed';
import styles from './GroupHeader.mscss';

const fasInfoCircleLg: FontAwesomeIconProps = { icon: faInfoCircle, size: 'lg' };
const fasSearchLg: FontAwesomeIconProps = {icon: faSearch, size: 'lg'};
const fasUserPlusLg: FontAwesomeIconProps = {icon: faUserPlus, size: 'lg'};

interface OwnProps {
    group: GroupEntity;
    groupLoadingState: LoadingState;
    hasAskedForMembership: boolean;
    isBlocked: boolean;
    isJoining: boolean;
    isMember: boolean;
    isModerator: boolean;
    isSearchOpen: boolean;
    isSubscribed: boolean;
    isFavorite: boolean;
    isSubscribing: boolean;
    location: Location;
    moderators: GroupMemberEntity[];
    permissions: string[];
    posts: BlogPostEntity[];
    recentContributors: number;
    requestUser: UserEntity;

    onAddToFavorites(groupId: number, userId: number);
    onInviteUser(groupId: number);
    onJoin(groupId: number, username: string);
    onLeave(groupId: number, username: string);
    onSubscribe(groupId: number, username: string);
    onRemoveFromFavorites(groupId: number, userId: number);
    onUnsubscribe(groupId: number, username: string);
    onSearchClick();
}

interface State {
    showFullDescription: boolean;
    hasLastKnownUsername: boolean;
}

export default class GroupHeader extends React.PureComponent<OwnProps, State> {

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            showFullDescription: false,
            hasLastKnownUsername: false,
        };
    }

    public componentDidMount() {
        if (typeof window !== 'undefined') {
            this.setState({
                hasLastKnownUsername: !!getLastKnownUsernameCookie(),
            });
        }
    }

    public renderGroupCover() {
        const { group, recentContributors } = this.props;

        return (
            <Cover
                icon={group.locked ? faLock : faUnlockAlt}
                photo={group.coverPhoto}
                title={group.name}
                subtitle={(recentContributors > 0) && (
                    <Interpolate
                        i18nKey={num_POSTS_AND_ANSWERS_IN_LAST_WEEK}
                        num={<span className="number">{recentContributors}</span>}
                        count={recentContributors}
                    />
                )}
            />
        );
    }

    public render() {
        const {
            group, groupLoadingState, hasAskedForMembership, isBlocked, isFavorite, isJoining,
            isMember, isModerator, isSearchOpen, isSubscribed, isSubscribing, location,
            onSearchClick, permissions, requestUser,
        } = this.props;

        const isAdmin = permissions.indexOf('forum.can_manage_forum') >= 0;
        const showInviteBtn = !group.isDomainGroup && (!group.locked || (group.locked && isModerator));
        const groupLoaded = isSuccess(groupLoadingState);

        return (
            <div className={styles.GroupHeader}>
                {this.renderGroupCover()}
                <>
                    <div className={styles.GroupHeader__footer}>
                        {!group.deletedAt && isMember && (
                            <Btn
                                key="subscribe"
                                type={BtnType.LightGreyOutline}
                                icon={isSubscribed ? fasBell : farBell}
                                onClick={isSubscribed ? this.handleOnUnsubscribe : this.handleOnSubscribe}
                                classNames={{label: styles.GroupHeader__bold}}
                                className={cx(
                                    styles.GroupHeader__subscribe,
                                    {[styles['GroupHeader__subscribe--on']]: isSubscribed},
                                )}
                                disabled={isSubscribing}
                            />
                        )}
                        {!group.deletedAt && isBlocked && (
                            <div className={styles.GroupHeader__footer__blocked}>
                                {GROUP_YOU_ARE_BLOCKED}
                            </div>
                        )}
                        {!group.deletedAt && !isBlocked && isMember && (
                            <Btn
                                key="favorite"
                                type={BtnType.LightGreyOutline}
                                icon={isFavorite ? fasStar : farStar}
                                onClick={!isFavorite ? this.handleOnAddToFavorites : this.handleOnRemoveFromFavorites}
                                classNames={{label: styles.GroupHeader__bold}}
                                className={
                                    cx(styles.GroupHeader__favorite,
                                    {[styles['GroupHeader__favorite--on']]: isFavorite},
                                )}
                                disabled={!groupLoaded}
                            />

                        )}
                        {!group.deletedAt && !isBlocked && (
                            <Btn
                                key="join"
                                type={(isMember || hasAskedForMembership) ? BtnType.LightGreyOutline : BtnType.BlueOutline}
                                label={
                                    isMember ? LEAVE_GROUP : (
                                        hasAskedForMembership ? GROUP_ASKED_TO_JOIN :
                                            (group.locked ? GROUP_ASK_TO_JOIN : JOIN_GROUP)
                                    )}
                                count={isMember ? undefined : group.membersCount}
                                countType={BtnCountType.Label}
                                onClick={isAuthenticated(requestUser) ? (isMember ? this.handleOnLeave : this.handleOnJoin) : null}
                                link={!isAuthenticated(requestUser) ?
                                    group.categoryId === SAME_DATE_GROUP_CATEGORY_ID && group.isDomainGroup ?
                                        this.state.hasLastKnownUsername ?
                                            url(loginUrl, {}, { next: location.pathname }) :
                                            url(signupUrl, {}, { next: location.pathname, ci: 'gd', group_name: group.name }) :
                                        url(loginUrl, {}, { next: location.pathname }) :
                                    null}
                                classNames={{label: styles.GroupHeader__bold}}
                                className={isMember
                                    ? styles['GroupHeader__join--isMember']
                                    : styles['GroupHeader__join--isNotMember']}
                                canBeVisuallyDisabled={!isModerator}
                                hasSpinner={isJoining}
                                disabled={isJoining || hasAskedForMembership}
                            />
                        )}
                        {(isMember || isAdmin) && (
                            <div key="rightButtons" className={styles.GroupHeader__rightButtons}>
                                {isMember && !group.deletedAt && showInviteBtn && (
                                    <Btn
                                        key="add-member"
                                        type={BtnType.BlueTextLight}
                                        icon={fasUserPlusLg}
                                        onClick={this.handleInviteUser}
                                    />
                                )}
                                <Btn
                                    key="about"
                                    className={!isMember && styles['GroupHeader__rightButtons__btn--admin']}
                                    type={BtnType.BlueTextLight}
                                    icon={fasInfoCircleLg}
                                    link={url(groupsGroupInfoUrl, { groupId: group.id })}
                                />
                                {!group.deletedAt && (
                                    <Btn
                                        key="search"
                                        className={cx(
                                            !isMember && styles['GroupHeader__rightButtons__btn--admin'],
                                            isSearchOpen && styles['GroupHeader__rightButtons__search--active'],
                                        )}
                                        type={BtnType.BlueTextLight}
                                        icon={fasSearchLg}
                                        onClick={onSearchClick}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </>
            </div>
        );
    }

    private handleInviteUser = () => {
        const { group, onInviteUser } = this.props;
        onInviteUser(group.id);
    };

    private handleOnJoin = () => {
        const { group, requestUser, onJoin } = this.props;
        onJoin(group.id, requestUser.username);
    };

    private handleOnLeave = () => {
        const { group, requestUser, onLeave } = this.props;
        onLeave(group.id, requestUser.username);
    };

    private handleOnSubscribe = () => {
        const { group, onSubscribe, requestUser } = this.props;
        onSubscribe(group.id, requestUser.username);
    };

    private handleOnUnsubscribe = () => {
        const { group, onUnsubscribe, requestUser } = this.props;
        onUnsubscribe(group.id, requestUser.username);
    };

    private handleOnAddToFavorites = () => {
        const { group, onAddToFavorites, requestUser } = this.props;
        onAddToFavorites(group.id, requestUser.id);
    };

    private handleOnRemoveFromFavorites = () => {
        const { group, onRemoveFromFavorites, requestUser } = this.props;
        onRemoveFromFavorites(group.id, requestUser.id);
    };

}

interface PlaceholderOwnProps {
    isLast?: boolean;
}

export const GroupHeaderPlaceholder: React.StatelessComponent<PlaceholderOwnProps> = ({ isLast }) => (
    <div className={cx(styles.GroupHeader, styles.GroupHeaderPlaceholder)}>
        <div className={styles.GroupHeader__cover}>
            <div className={cx(styles.GroupHeader__cover_photo, styles.GroupHeaderPlaceholder__cover_photo)} />
            <div className={styles.GroupHeader__cover_text}>
                <LoadingPlaceholder className={styles.GroupHeader__cover_group_name} width={180} height={20} />
                <LoadingPlaceholder className={styles.GroupHeader__cover_posts} width={130} height={14} />
            </div>
        </div>
        <div className={styles.GroupHeader__footer}>
            <div className={cx(styles.GroupHeader__footer__subscribe, styles.GroupHeaderPlaceholder__subscribe)}>
                <LoadingPlaceholder width={120} height={25} />
            </div>
            <div className={styles.GroupHeaderPlaceholder__about}>
                <LoadingPlaceholder width={65} height={25} />
            </div>
        </div>
    </div>
);
GroupHeaderPlaceholder.displayName = 'GroupHeaderPlaceholder';
